import React, { Fragment, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { AccordionDetails, Box, CircularProgress, Divider, Tab, Tabs, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import { grey } from '@mui/material/colors'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { isPaymentsV15FeatureAvailable } from 'data/launch-darkly/featureSelectors'
import T from 'T'
import TabPanel from 'components/common/TabPanel'
import PaymentActivity from 'components/customer-details/content/common/PaymentActivity'
import HHSectionPlaceholder from 'components/common/HHSectionPlaceholder'
import EmailActivity from 'components/customer-details/content/common/EmailActivity'
import PostingActivity from 'components/customer-details/content/common/PostingActivity'
import { INVOICE_ACTIVITY_TABS_ID } from 'components/customer-details/content/invoices/settings'
import { PAYMENT_METHODS, PAYMENT_STATUS } from 'settings/constants/payment'
import ActivityHeader from 'components/customer-details/content/common/ActivityHeader'
import { useLazyGetInvoiceVersionsQuery } from 'api/entity-version/entityVersionCrud'

const { PAYMENT_TAB_ID, EMAIL_TAB_ID, POSTING_TAB_ID } = INVOICE_ACTIVITY_TABS_ID
const { ACCOUNT_CREDIT } = PAYMENT_METHODS
const { APPROVED } = PAYMENT_STATUS
const InvoiceActivityFeed = ({
  payments = [],
  accountCredits = [],
  emailEvents = [],
  invoiceId,
  onSelectedTabChange,
  selectedTab,
  Component = AccordionDetails,
}) => {
  const flags = useFlags()
  const isPaymentsV15Enabled = isPaymentsV15FeatureAvailable({ flags })
  const [getInvoiceVersions, { data: invoiceVersionsData, isLoading: isLoadingVersions }] = useLazyGetInvoiceVersionsQuery()

  const invoiceVersions = useMemo(() => {
    const versions = invoiceVersionsData?.versions || []
    return [...versions].sort((a, b) => b.rev - a.rev)
  }, [invoiceVersionsData])

  useEffect(() => {
    if (selectedTab === POSTING_TAB_ID && invoiceId) {
      getInvoiceVersions({ invoiceId })
    }
  }, [selectedTab, invoiceId, getInvoiceVersions])

  const allPayments = useMemo(() => {
    if (isPaymentsV15Enabled) {
      return payments.map(paymentSummaryItem => {
        const { paymentMethod, appliedAmount } = paymentSummaryItem
        const isAccountCredit = paymentMethod === ACCOUNT_CREDIT
        if (isAccountCredit) {
          return {
            ...paymentSummaryItem,
            transactionStatus: APPROVED,
            transactionAmount: appliedAmount,
          }
        }
        return paymentSummaryItem
      })
    }
    return [
      ...payments,
      ...accountCredits.map(accountCredit => ({
        isAccountCredit: true,
        paymentMethod: ACCOUNT_CREDIT,
        transactionAmountCents: accountCredit?.amountCents,
        transactionPostedDate: accountCredit?.createdAt,
        transactionStatus: APPROVED,
      })),
    ]
  }, [payments, accountCredits, isPaymentsV15Enabled])

  return (
    <Component
      sx={{
        px: 2,
        pt: 1,
        pb: 0,
        backgroundColor: grey[50],
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderColor: 'border.light',
      }}
    >
      <Box width="100%" pl={4.5}>
        <Typography variant="h6" fontWeight={500} my={1.5}>
          {T.ACTIVITY}
        </Typography>

        <Tabs value={selectedTab} onChange={onSelectedTabChange} sx={{ mb: 2, button: { textTransform: 'capitalize' } }}>
          <Tab value={PAYMENT_TAB_ID} label={T.PAYMENT} />
          <Tab value={EMAIL_TAB_ID} label={T.EMAIL} />
          <Tab value={POSTING_TAB_ID} label={T.POSTING_TAB_LABEL} />
        </Tabs>

        <TabPanel value={selectedTab} index={PAYMENT_TAB_ID}>
          {Array.isArray(allPayments) && allPayments.length > 0 ? (
            <>
              <ActivityHeader statusLabel="Payment status" totalLabel="Payment total" />
              <Divider sx={{ width: '100%' }} />
              <Grid container>
                {allPayments.map((payment, index) => (
                  <Fragment key={payment.id ?? index}>
                    <PaymentActivity payment={payment} showAppliedColumn align="inherit" marginY={2} />
                    {allPayments.length - 1 !== index && <Divider sx={{ width: '100%' }} />}
                  </Fragment>
                ))}
              </Grid>
            </>
          ) : (
            <Box width="100%">
              <HHSectionPlaceholder title={T.NO_PAYMENT_ACTIVITY_FOUND} />
            </Box>
          )}
        </TabPanel>
        <TabPanel value={selectedTab} index={EMAIL_TAB_ID}>
          {Array.isArray(emailEvents) && emailEvents.length > 0 ? (
            <>
              <Grid container spacing={2.5} mb={1}>
                <Grid minWidth={120} size="auto">
                  <Typography variant="body2">{T.STATUS}</Typography>
                </Grid>
                <Grid size="grow" />
                <Grid minWidth={120} size="auto" sx={{ textAlign: 'center' }}>
                  <Typography variant="body2">{T.EMAIL_SEND_TYPE}</Typography>
                </Grid>
              </Grid>
              <Divider sx={{ width: '100%' }} />
              <Grid container rowGap={2} my={2}>
                {emailEvents.map((email, index) => (
                  <Fragment key={email.id ?? index}>
                    <EmailActivity email={email} />
                    {emailEvents.length - 1 !== index && <Divider sx={{ width: '100%' }} />}
                  </Fragment>
                ))}
              </Grid>
            </>
          ) : (
            <Box width="100%">
              <HHSectionPlaceholder title={T.NO_EMAIL_ACTIVITY_FOUND} />
            </Box>
          )}
        </TabPanel>
        <TabPanel value={selectedTab} index={POSTING_TAB_ID}>
          {isLoadingVersions && (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!isLoadingVersions && Array.isArray(invoiceVersions) && invoiceVersions.length > 0 && (
            <>
              <Grid container spacing={2.5} mb={1}>
                <Grid minWidth={100} size="auto">
                  <Typography variant="body2">{T.STATUS}</Typography>
                </Grid>
                <Grid size="grow">
                  <Typography variant="body2">{T.ACTION}</Typography>
                </Grid>
              </Grid>
              <Divider sx={{ width: '100%' }} />
              <Grid container rowGap={2} my={2}>
                {invoiceVersions.map((version, index) => {
                  // Get the previous version (next in array since sorted desc by rev)
                  const previousVersion = invoiceVersions[index + 1]
                  return (
                    <Fragment key={version.rev}>
                      <PostingActivity version={version} previousVersion={previousVersion} />
                      {invoiceVersions.length - 1 !== index && <Divider sx={{ width: '100%' }} />}
                    </Fragment>
                  )
                })}
              </Grid>
            </>
          )}
          {!isLoadingVersions && (!Array.isArray(invoiceVersions) || invoiceVersions.length === 0) && (
            <Box width="100%">
              <HHSectionPlaceholder title="No posting activity available" />
            </Box>
          )}
        </TabPanel>
      </Box>
    </Component>
  )
}

InvoiceActivityFeed.propTypes = {
  payments: PropTypes.array,
  accountCredits: PropTypes.array,
  emailEvents: PropTypes.array,
  invoiceId: PropTypes.string,
  Component: PropTypes.element,
  selectedTab: PropTypes.string.isRequired,
  onSelectedTabChange: PropTypes.func.isRequired,
}

export default InvoiceActivityFeed
